import React from "react";
import { DateTime } from "luxon";
import { Modal } from "react-bootstrap";
import { City, Country } from "../types/Lookups";
import Clock from "react-clock";
import "react-clock/dist/Clock.css";

interface TimeZoneDisplayProps {
  dateTime: string | null;
  show: boolean;
  onHide: () => void;
  cities?: (City | null)[];
  countries?: (Country | null)[];
  label?: string;
}

const getFlagUrl = (countryCode: string | null | undefined): string | null => {
  if (!countryCode || countryCode.length !== 2) {
    return null;
  }
  return `https://flagcdn.com/w20/${countryCode.toLowerCase()}.png`;
};

/**
 * Component to display a date/time in multiple timezones
 * Shows GMT, Palestine (Jerusalem), Egypt (Cairo), and all provided cities' timezones
 */
const TimeZoneDisplay: React.FC<TimeZoneDisplayProps> = ({
  dateTime,
  show,
  onHide,
  cities = [],
  countries = [],
  label = "Date/Time",
}) => {
  // Parse the datetime (assuming it's in UTC/GMT)
  const getFormattedTimes = () => {
    if (!dateTime) {
      return null;
    }

    // Parse as UTC
    const dt = DateTime.fromISO(dateTime, { zone: "utc" });

    if (!dt.isValid) {
      return null;
    }

    const timezones = [
      {
        name: "GMT (Greenwich)",
        timezone: "GMT",
        flag: "🌐",
        flagUrl: null,
      },
      {
        name: "Palestine (Jerusalem)",
        timezone: "Asia/Jerusalem",
        flag: null,
        flagUrl: getFlagUrl("PS"),
      },
      {
        name: "Egypt (Cairo)",
        timezone: "Africa/Cairo",
        flag: null,
        flagUrl: getFlagUrl("EG"),
      },
    ];

    // Add timezones for all cities provided
    cities.forEach((city, index) => {
      const country = countries[index];
      if (city?.timeZone?.name && country) {
        // Only add if it's not already in the list
        if (!timezones.find((tz) => tz.timezone === city.timeZone!.name)) {
          timezones.push({
            name: `${city.name}, ${country.name}`,
            timezone: city.timeZone.name,
            flag: null,
            flagUrl: getFlagUrl(country.iso2Code),
          });
        }
      }
    });

    return timezones.map((tz) => {
      const localTime = dt.setZone(tz.timezone);
      return {
        ...tz,
        dateTime: localTime.toFormat("yyyy-MM-dd"),
        time: localTime.toFormat("HH:mm"),
        fullDateTime: localTime.toFormat("EEEE, MMMM dd, yyyy HH:mm:ss"),
        offset: localTime.toFormat("ZZ"),
        dateObject: localTime.toJSDate(), // For react-clock
      };
    });
  };

  const formattedTimes = getFormattedTimes();

  return (
    <Modal show={show} onHide={onHide} centered size="xl" className="timezone-modal">
      <Modal.Header className="py-2" closeButton>
        <Modal.Title>
          <i className="bi bi-hourglass-split me-2" aria-hidden="true"></i>
          {label ? `${label} — time zones` : "Time zones"}
          {formattedTimes && (
            <span className="d-block mt-1" style={{ fontSize: "var(--fs-xs)", fontWeight: "normal", color: "var(--text-muted)" }}>
              Original time (UTC): {DateTime.fromISO(dateTime!, { zone: "utc" }).toFormat("EEEE, MMMM dd, yyyy HH:mm:ss")} UTC
            </span>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ backgroundColor: "var(--background-color)" }}>
        {!dateTime && (
          <p className="mb-0" style={{ color: "var(--text-muted)" }}>No date and time picked yet.</p>
        )}
        {dateTime && !formattedTimes && (
          <p className="mb-0" style={{ color: "var(--text-muted)" }}>This date and time can't be read. Pick it again in the field.</p>
        )}
        {formattedTimes && (
          <div>
            <div className="row">
              {formattedTimes.map((tz, index) => (
                <div key={index} className="col-md-6 col-lg-3 mb-3">
                  <div className="card h-100 shadow-sm timezone-card" style={{ backgroundColor: "var(--background-color)", borderColor: "var(--control-background-color)" }}>
                    <div className="text-center p-2">
                      <h6 className="card-title mb-2" style={{ fontSize: "var(--fs-md)", color: "var(--color)" }}>
                        <span className="me-1" style={{ fontSize: "var(--fs-md)" }}>
                          {tz.flagUrl ? (
                            <img src={tz.flagUrl} alt="" style={{ width: '20px', verticalAlign: 'middle' }} className="me-1" />
                          ) : (
                            tz.flag
                          )}
                        </span>
                        <span style={{ fontSize: "var(--fs-md)" }}>
                        {tz.name}
                        <br/>
                        UTC {tz.offset}
                        </span>
                      </h6>

                      {/* Analog Clock */}
                      <div className="d-flex justify-content-center mb-2">
                        <Clock
                          value={tz.time}
                          size={120}
                          renderNumbers={true}
                        />
                      </div>

                      {/* Digital Time Display */}
                      <div className="text-center mb-2">
                        <div style={{ fontSize: "var(--fs-md)", fontWeight: "bold", color: "var(--color)" }}>
                          {tz.time}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default TimeZoneDisplay;

